import { useAuth0 } from "@auth0/auth0-react";
import {
  BookOpen,
  ChevronDown,
  Heart,
  Info,
  LogOut,
  Megaphone,
  Menu,
  MessageCircle,
  Scale,
  Shield,
  CircleCheckBig,
  User,
  Users,
  X,
  GraduationCap,
  HelpCircle,
} from "lucide-react";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useApiWithAuth } from "../hooks/useAuth.js";
import { useMyApplications } from "../hooks/useMyApplications.js";
import { useUserSync } from "../hooks/useUserSync.js";
// import { useComparison } from "../context/ComparisonProvider.js";
import { IS_NATIVE_APP, isIosBlockedPath } from "../config/appTarget.js";
import { getLogoutReturnTo } from "../auth/authConfig.js";
import Dropdown from "./Dropdown.js";
import {
  CatIconFilled,
  DogIconFilled,
  HomeIconFilled,
  PawmatIconOutlined,
} from "./Icons/CustomIcons.js";
import { loginWithReturn } from "../lib/loginWithReturn.js";
import NotificationBell from "../features/notifications/NotificationBell.js";
import { LanguageSwitcher } from "../i18n/LanguageSwitcher.js";
import { resolveProfileAvatar } from "../lib/profileAvatar.js";
import ConfirmModal from "../Components/ConfirmModal.js";
import { PAGE_GUTTER_CLASS } from "./PageContainer.js";
import UserDropdown from "./UserDropdown.js";

const Navbar = memo(() => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileProfileOpen, setIsMobileProfileOpen] = useState(false);
  const isQaEnvironment = import.meta.env.VITE_APP_ENV === "qa";
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loginWithRedirect, logout, isLoading } =
    useAuth0();
  const { user: dbUser } = useUserSync();
  const {
    isAnyAdmin,
    user: apiUser,
    userKennelId,
    isKennelAdmin,
    isAdvertiser,
    canManageAnimals,
    isPermissionsLoading,
    apiCallWithAuth,
  } = useApiWithAuth();

  // Profile avatar: uploaded photo > real social photo > a stable random dog/cat
  // (never Auth0 initials). Seeded by user id so it doesn't change on re-render.
  const avatarSrc = useMemo(
    () =>
      resolveProfileAvatar({
        dbAvatarUrl: dbUser?.avatarUrl,
        authPicture: user?.picture,
        seed: user?.sub,
      }),
    [dbUser?.avatarUrl, user?.picture, user?.sub],
  );

  const kennelName: string | null = null;

  // Dismissible advertiser nav button (localStorage flag)
  const [advertiserNavHidden, setAdvertiserNavHidden] = React.useState(
    () => localStorage.getItem("hideAdvertiserNav") === "true",
  );
  const [showDismissConfirm, setShowDismissConfirm] = React.useState(false);
  function dismissAdvertiserNav() {
    localStorage.setItem("hideAdvertiserNav", "true");
    setAdvertiserNavHidden(true);
    window.dispatchEvent(new Event("advertiserNavVisibilityChanged"));
  }
  // Keep the button's visibility in sync when the flag changes elsewhere
  // (e.g. the restore toggle in Settings) without requiring a full reload.
  React.useEffect(() => {
    const sync = () =>
      setAdvertiserNavHidden(
        localStorage.getItem("hideAdvertiserNav") === "true",
      );
    window.addEventListener("advertiserNavVisibilityChanged", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("advertiserNavVisibilityChanged", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  // Show button when: authenticated, not canManageAnimals, permissions loaded
  const showAdvertiserButton =
    isAuthenticated &&
    !isPermissionsLoading &&
    !canManageAnimals &&
    !advertiserNavHidden;

  // Detect an in-progress candidatura so the button points to its status
  // instead of the apply flow (avoids a confusing duplicate application).
  const { pendingApplication } = useMyApplications({
    enabled:
      isAuthenticated &&
      !isPermissionsLoading &&
      !canManageAnimals &&
      !isAdvertiser,
  });

  function getAdvertiserPath() {
    if (isAdvertiser) return "/meu-anuncio";
    if (pendingApplication) return pendingApplication.statusPath;
    return "/quero-anunciar";
  }
  function getAdvertiserLabel() {
    if (isAdvertiser) return t("nav.advertiserAds");
    if (pendingApplication) return t("nav.myApplication");
    return t("nav.wantToAdvertise");
  }

  // Function to check if a menu item is active
  const isActiveItem = useCallback(
    (item: any) => {
      const currentPath = location.pathname;

      // Direct path match
      if (currentPath === item.href) return true;

      // Check dropdown items for active state
      if (item.hasDropdown && item.dropdownItems) {
        return item.dropdownItems.some(
          (dropdownItem: any) =>
            currentPath === dropdownItem.href ||
            (dropdownItem.href !== "/" &&
              currentPath.startsWith(dropdownItem.href)),
        );
      }

      // For non-dropdown items, check if current path starts with item path (but not for home)
      if (item.href !== "/" && currentPath.startsWith(item.href)) return true;

      return false;
    },
    [location.pathname],
  );

  const isAdminRoute = location.pathname.startsWith("/painel-admin")
  const isLandingPage = location.pathname === "/" || location.pathname === "/adotar"

  // const { comparisonList } = useComparison();

  // Debug: expose auth claims and computed flags to the browser console
  // Debug logging removed to avoid leaking sensitive user data in production.

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".user-menu-container")) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isUserMenuOpen]);

  // Memoize navigation handlers
  const handleLogoClick = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const handleLogin = useCallback(() => {
    loginWithReturn(loginWithRedirect);
  }, [loginWithRedirect]);

  const handleLogout = useCallback(() => {
    logout({ logoutParams: { returnTo: getLogoutReturnTo() } });
    setIsUserMenuOpen(false);
  }, [logout]);

  const handleMobileLogin = useCallback(() => {
    loginWithReturn(loginWithRedirect);
    setIsMenuOpen(false);
  }, [loginWithRedirect]);

  const handleMobileLogout = useCallback(() => {
    logout({ logoutParams: { returnTo: getLogoutReturnTo() } });
    setIsMenuOpen(false);
  }, [logout]);

  const Favorites = (
    <div className="p-2 rounded-xl group-hover:bg-golden transition-colors duration-300">
      <span className="text-4xl font-bold text-golden group-hover:text-golden transition-colors duration-300">
        <Heart />
      </span>
    </div>
  );

  const AdminIcon = (
    <div className="p-2 rounded-xl group-hover:bg-golden transition-colors duration-300">
      <span className="text-4xl font-bold text-golden group-hover:text-golden transition-colors duration-300">
        <Shield />
      </span>
    </div>
  );

  const filterIosNav = useCallback(
    <T extends { href: string }>(items: T[]): T[] => {
      if (!IS_NATIVE_APP) return items;
      return items.filter((item) => !isIosBlockedPath(item.href));
    },
    [],
  );

  const menuItems = useMemo(() => {
    const baseItems = [
      {
        label: t("nav.adopt"),
        href: "/adotar",
        icon: <HomeIconFilled size={25} />,
        hasDropdown: true,
        dropdownItems: [
          {
            label: t("nav.partnerShelters"),
            href: "/canis",
            icon: <Users className="w-4 h-4" />,
          },
          {
            label: t("nav.privateAdvertisers"),
            href: "/anunciantes-particulares",
            icon: <User className="w-4 h-4" />,
          },
          {
            label: t("nav.ownerTutorial"),
            href: "/tutorial-dono",
            icon: <GraduationCap className="w-4 h-4" />,
          },
          {
            label: t("nav.whyAdopt"),
            href: "/porque-adotar",
            icon: <HelpCircle className="w-4 h-4" />,
          },
          {
            label: t("nav.blog"),
            href: "/blog",
            icon: <BookOpen className="w-4 h-4" />,
          },
          {
            label: t("nav.aboutPawseum"),
            href: "/sobre-pawseum",
            icon: <Info className="w-4 h-4" />,
          },
          {
            label: t("nav.howToAdvertise"),
            href: "/quero-anunciar",
            icon: <Megaphone className="w-4 h-4" />,
          },
        ],
      },
      {
        label: t("animal.dogs"),
        href: "/caes?page=1",
        icon: <DogIconFilled size={30} />,
        hasDropdown: true,
        dropdownItems: [
          {
            label: t("nav.available"),
            href: "/caes?page=1",
            icon: <Heart className="w-4 h-4" />,
          },
          {
            label: t("nav.adopted"),
            href: "/caes/adotados?page=1",
            icon: <CircleCheckBig size={16} />,
          },
        ],
      },
      {
        label: t("animal.cats"),
        href: "/gatos?page=1",
        icon: <CatIconFilled size={25} />,
        hasDropdown: true,
        dropdownItems: [
          {
            label: t("nav.available"),
            href: "/gatos?page=1",
            icon: <Heart className="w-4 h-4" />,
          },
          {
            label: t("nav.adopted"),
            href: "/gatos/adotados?page=1",
            icon: <CircleCheckBig size={16} />,
          },
        ],
      },
      {
        label: t("nav.favorites"),
        href: "/favoritos",
        icon: <Heart fill="#f2c94c" size={24} />,
        hasDropdown: true,
        dropdownItems: [
          {
            label: t("nav.matches"),
            href: "/pawmatch/lista",
            icon: <Heart className="w-4 h-4" fill="currentColor" />,
          },
          {
            label: t("nav.likes"),
            href: "/favoritos",
            icon: <Heart className="w-4 h-4" />,
          },
        ],
      },
      // {
      //   label: "Comparar",
      //   href: "/comparar",
      //   icon: <Scale size={25} />,
      //   badge: comparisonList.length > 0 ? comparisonList.length : undefined
      // },
    ];

    baseItems.splice(-1, 0, {
      label: "PawMatch",
      href: "/pawmatch",
      icon: (
        <span
          className={`inline-flex items-center justify-center overflow-hidden ${
            isAdminRoute ? "[&_svg]:!h-7 [&_svg]:!w-10" : "[&_svg]:!h-8 [&_svg]:!w-11"
          }`}
        >
          <PawmatIconOutlined fill="#f2c94c" size={isAdminRoute ? 40 : 42} />
        </span>
      ),
      hasDropdown: true,
      dropdownItems: [
        {
          label: t("nav.findYourMatch"),
          href: "/pawmatch",
          icon: <Heart className="w-4 h-4" />,
        },
        {
          label: t("nav.pawmatchTutorial"),
          href: "/pawmatch/tutorial",
          icon: <GraduationCap className="w-4 h-4" />,
        },
      ],
    });

    // Add admin panel only if user is admin
    if (isAnyAdmin) {
      baseItems.push({
        label: t("nav.adminPanel"),
        href: "/painel-admin",
        icon: AdminIcon,
        hasDropdown: false,
        dropdownItems: [],
      });
    }

    return baseItems.map((item) => {
      if (!item.hasDropdown || !item.dropdownItems) return item;
      return {
        ...item,
        dropdownItems: filterIosNav(item.dropdownItems),
      };
    });
  }, [filterIosNav, isAdminRoute, isAnyAdmin, t]);

  // Intentionally silent in production; enable explicit debugging only when needed.

  return (
    <nav
      className={`pawseum-global-navbar absolute top-0 left-0 right-0 z-[9999] ${
        isLandingPage ? "" : "bg-main-purple backdrop-blur-md border-b border-main-purple/20 shadow-lg"
      }`}
      style={
        IS_NATIVE_APP ? { paddingTop: "env(safe-area-inset-top)" } : undefined
      }
    >
      {isLandingPage && (
        // Landing only: solid purple for the bar's height, eased to transparent below it.
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 -bottom-14 -z-10 bg-global-nav-glass"
        />
      )}
      {isQaEnvironment && (
        <div
          className="pointer-events-none fixed bottom-3 right-3 z-[10000] rounded-full border border-main-purple bg-golden px-3 py-1 text-xs font-bold text-main-purple shadow-lg"
          aria-hidden="true"
        >
          QA
        </div>
      )}
      {" "}
      <div className={PAGE_GUTTER_CLASS}>
        <div className={`flex justify-between items-center h-16 ${isAdminRoute ? "lg:h-12" : "sm:h-20"}`}>
          {/* Logo */}
          <button
            className="z-10 flex-shrink-0 cursor-pointer hover:scale-105 transition-all duration-300 focus:outline-none rounded-lg flex items-center lg:-translate-x-4 xl:-translate-x-5"
            onClick={handleLogoClick}
            aria-label={t("nav.goHome")}
          >
            <img
              src="/assets/logos/logo.svg"
              alt={t("nav.logoAlt")}
              loading="eager"
              decoding="sync"
              className={` ${isAdminRoute ? "md:w-[100px] " : "w-[108px] h-[36px] sm:w-[120px] sm:h-[40px] md:w-[132px] md:h-[44px] lg:w-[150px] lg:h-[50px]"} `}
            />
          </button>

          {/* Desktop Menu */}
          <div className="hidden pt-1 lg:flex items-center space-x-1 xl:space-x-3 md:-translate-x-3 xl:-translate-x-4">
            {menuItems
              .filter((item) => item.href !== "/painel-admin")
              .map((item) => {
              const isActive = isActiveItem(item);
              return item.hasDropdown ? (
                <Dropdown
                  key={item.label}
                  trigger={
                    <div
                      className={`text-golden hover:text-golden transition-colors duration-300 font-medium relative group flex items-center justify-center xl:min-w-[120px] lg:min-w-[90px] min-w-[80px] ${isAdminRoute ? "h-9" : "h-12"} ${isActive ? "text-golden" : ""}`}
                    >
                      <span
                        className={`${isActive ? "hidden" : "group-hover:hidden"} flex items-center justify-center w-full h-full ${isAdminRoute ? "[&_svg]:w-5 [&_svg]:h-5" : "[&_svg]:w-6 [&_svg]:h-6"}`}
                      >
                        {item.icon ? item.icon : item.label}
                      </span>
                      <span
                        className={`${isActive ? "flex" : "hidden group-hover:flex"} items-center justify-center w-full h-full ${isAdminRoute ? "text-xs" : ""}`}
                      >
                        {item.label}
                      </span>
                      <span
                        className={`absolute -bottom-1 left-0 ${isAdminRoute ? "h-0.5" : "h-0.5"} bg-golden transition-all duration-300 ${isActive ? "w-full" : "w-0 group-hover:w-full"} group-hover:bg-golden`}
                      />
                    </div>
                  }
                  items={item.dropdownItems || []}
                  position="center"
                  hoverOnDesktop={true}
                />
              ) : (
                <button
                  key={item.label}
                  onClick={() => navigate(item.href)}
                  className={`text-golden hover:text-golden transition-colors duration-300 font-medium relative group flex items-center justify-center xl:min-w-[120px] lg:min-w-[90px] min-w-[80px] ${isAdminRoute ? "h-9" : "h-12"} focus:outline-none focus:ring-2 focus:ring-golden rounded-lg ${isActive ? "text-golden" : ""}`}
                  aria-label={t("nav.goTo", { label: item.label })}
                >
                  <span
                    className={`${isActive ? "hidden" : "group-hover:hidden"} flex items-center justify-center w-full h-full relative ${isAdminRoute ? "[&_svg]:w-5 [&_svg]:h-5" : "[&_svg]:w-6 [&_svg]:h-6"}`}
                  >
                    <span className="sr-only">{item.label}</span>
                    {item.icon ? item.icon : item.label}
                  </span>
                  <span
                    className={`${isActive ? "flex" : "hidden group-hover:flex"} items-center justify-center w-full h-full relative ${isAdminRoute ? "text-xs" : ""}`}
                  >
                    {item.label}
                  </span>
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-golden transition-all duration-300 ${isActive ? "w-full" : "w-0 group-hover:w-full"} group-hover:bg-golden`}
                  />
                </button>
              );
            })}
          </div>

          {/* Sign In / User Profile */}
          {/* TODO(ios): this top Navbar is hidden on native (hideTopNav === IS_NATIVE_APP),
              so the image-only profile button + notification bell here are web-only.
              Verify the iOS equivalent (avatar + notifications entry) via MobileBottomNav
              once a device/simulator is available — not testable right now. */}
          <div className={`hidden lg:flex items-center ${isAdminRoute ? "space-x-2" : "space-x-4"}`}>
            <LanguageSwitcher variant="navbar" />
            {isAuthenticated && isAnyAdmin && (
              <button
                onClick={() => navigate("/painel-admin")}
                className={`flex items-center gap-1.5 border-2 shadow-xl rounded-xl font-semibold transition-all duration-300 hover:scale-105 ${isAdminRoute ? "py-1 px-2.5 text-sm bg-golden text-my-black border-golden" : "py-1.5 px-3 text-golden border-golden hover:bg-golden hover:text-my-black"}`}
                aria-label={t("nav.goToAdminPanel")}
              >
                <span>Admin</span>
                <Shield className={isAdminRoute ? "w-4 h-4" : "w-5 h-5"} aria-hidden="true" />
              </button>
            )}
            {!isAuthenticated ? (
              <button
                onClick={handleLogin}
                disabled={isLoading}
                className={`flex items-center space-x-2 text-lg border-2 shadow-xl text-golden border-golden hover:bg-vanilla hover:text-my-black active:border-gradient-blue text-harvest-golden pl-1.5 pr-4 rounded-xl font-semibold transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${isAdminRoute ? "py-1 text-sm" : "py-1.5"}`}
                aria-label={t("nav.signInToAccount")}
              >
                <img
                  src={avatarSrc}
                  alt=""
                  className={`rounded-full object-cover border-2 border-main-purple ${isAdminRoute ? "w-7 h-7" : "w-9 h-9"}`}
                  role="presentation"
                  referrerPolicy="no-referrer"
                />
                <span>{isLoading ? t("nav.loadingShort") : t("nav.signIn")}</span>
              </button>
            ) : (
              // Same box as before: profile pic + (bell in place of the name).
              <div className={`flex items-center border-2 border-golden shadow-xl rounded-xl ${isAdminRoute ? "gap-0.5 px-1 py-0.5" : "gap-1 px-1.5 py-1"}`}>
                <UserDropdown
                  isOpen={isUserMenuOpen}
                  onToggle={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  onClose={() => setIsUserMenuOpen(false)}
                  avatarSrc={avatarSrc}
                  user={user}
                  kennelName={kennelName}
                  isKennelAdmin={isKennelAdmin}
                  isPermissionsLoading={isPermissionsLoading}
                  userKennelId={userKennelId}
                  showAdvertiserButton={showAdvertiserButton}
                  advertiserPath={getAdvertiserPath()}
                  advertiserLabel={getAdvertiserLabel()}
                  onLogout={handleLogout}
                />
                <NotificationBell />
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2.5">
            {isAuthenticated && <NotificationBell />}
            <button
              className="mt-1 text-golden hover:text-main-purple transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-golden rounded-lg p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
              aria-expanded={isMenuOpen}
              aria-haspopup="menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" aria-hidden="true" />
              ) : (
                <Menu className="w-6 h-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <nav
            className="lg:hidden bg-hero-gradient absolute top-16 left-0 right-0 bg-goldenen-beige/95 backdrop-blur-md border-b border-tabby-light shadow-lg z-[999999]"
            role="navigation"
            aria-label={t("nav.mobileMainMenu")}
          >
            <div className="px-4 py-6 space-y-4">
              {/* Priority mobile actions: Quero anunciar + expandable profile first */}
              {isAuthenticated && (
                <div className="space-y-2 pb-4 mb-2 border-b border-tabby-light">
                  {showAdvertiserButton && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          navigate(getAdvertiserPath());
                          setIsMenuOpen(false);
                        }}
                        className="flex-1 flex items-center justify-center space-x-2 bg-golden text-my-black px-6 py-3 rounded-xl font-semibold transition-all duration-300"
                      >
                        <Megaphone className="w-4 h-4" />
                        <span>{getAdvertiserLabel()}</span>
                      </button>
                      {isAdvertiser && (
                        <button
                          onClick={() => setShowDismissConfirm(true)}
                          className="px-3 py-3 bg-gray-100 rounded-xl text-gray-400 text-sm"
                          aria-label={t("actions.hide")}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  )}
                  {/* Expandable profile: tap to reveal "Sobre mim" */}
                  <button
                    onClick={() => setIsMobileProfileOpen((v) => !v)}
                    className="w-full flex items-center space-x-3 px-3 py-2 rounded-xl hover:bg-golden/10 transition-colors duration-200"
                    aria-expanded={isMobileProfileOpen}
                    aria-label={t("nav.myProfile")}
                  >
                    <img
                      src={avatarSrc}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover border-2 border-main-purple"
                      role="presentation"
                      referrerPolicy="no-referrer"
                    />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-golden truncate">
                        {user?.name}
                      </p>
                      <p className="text-xs text-gray-600 truncate">
                        {user?.email}
                      </p>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-golden flex-shrink-0 transition-transform duration-200 ${
                        isMobileProfileOpen ? "rotate-180" : ""
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                  {isMobileProfileOpen && (
                    <div className="ml-3">
                      <button
                        onClick={() => {
                          navigate("/perfil");
                          setIsMenuOpen(false);
                        }}
                        className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-golden hover:bg-golden/10 rounded-lg transition-colors duration-200"
                      >
                        <User className="w-4 h-4" aria-hidden="true" />
                        <span>{t("nav.aboutMe")}</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
              {(() => {
                const adminIdx = menuItems.findIndex(
                  (i) => i.href === "/painel-admin",
                );
                if (adminIdx <= 0) return menuItems;
                const reordered = [...menuItems];
                const [adminItem] = reordered.splice(adminIdx, 1);
                reordered.unshift(adminItem);
                return reordered;
              })().map((item) => {
                const isActive = isActiveItem(item);
                return item.hasDropdown ? (
                  <div key={item.label} className="space-y-2">
                    <div
                      className={`flex items-center space-x-3 ml-3 font-medium py-2 border-b border-tabby-light/30 ${isActive ? "text-golden" : "text-golden"}`}
                    >
                      {item.icon && (
                        <div className="flex-shrink-0">{item.icon}</div>
                      )}
                      <span>{item.label}</span>
                    </div>
                    <div className="ml-8 space-y-2">
                      {item.dropdownItems?.map((dropdownItem) => {
                        const isDropdownActive =
                          location.pathname === dropdownItem.href ||
                          (dropdownItem.href !== "/" &&
                            location.pathname.startsWith(dropdownItem.href));
                        return (
                          <button
                            key={dropdownItem.label}
                            onClick={() => {
                              navigate(dropdownItem.href);
                              setIsMenuOpen(false);
                            }}
                            className={`flex items-center space-x-3 hover:text-main-purple transition-colors duration-300 font-medium py-1 text-sm w-full text-left ${isDropdownActive ? "text-golden bg-golden/10 px-2 py-2 rounded" : "text-golden/80"}`}
                          >
                            {dropdownItem.icon && (
                              <div className="flex-shrink-0">
                                {dropdownItem.icon}
                              </div>
                            )}
                            <span>{dropdownItem.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <button
                    key={item.label}
                    onClick={() => {
                      navigate(item.href);
                      setIsMenuOpen(false);
                    }}
                    className={`flex items-center space-x-3 ml-3 hover:text-main-purple transition-colors duration-300 font-medium py-2 w-full text-left ${isActive ? "text-golden bg-golden/10 px-2 py-2 rounded" : "text-golden"}`}
                  >
                    {item.icon && (
                      <div className="flex-shrink-0 relative">{item.icon}</div>
                    )}
                    <span className="flex items-center">{item.label}</span>
                  </button>
                );
              })}

              <LanguageSwitcher variant="navbar" className="justify-center mt-4" />

              {/* Mobile Auth Section */}
              {!isAuthenticated ? (
                <button
                  onClick={handleMobileLogin}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center space-x-2 bg-main-purple border-2 border-main-purple hover:bg-lighter-blue hover:border-lighter-blue active:border-gradient-blue text-harvest-golden px-6 py-3 rounded-xl font-semibold transition-all duration-300 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <User className="w-4 h-4" />
                  <span>{isLoading ? t("nav.loadingShort") : t("nav.signIn")}</span>
                </button>
              ) : (
                <div className="mt-4 pt-4 border-t border-tabby-light">
                  {/* Kennel admin: show kennel name + link to panel */}
                  {isKennelAdmin && !isPermissionsLoading && (
                    <>
                      <button
                        onClick={() => {
                          navigate("/painel-admin");
                          setIsMenuOpen(false);
                        }}
                        className="w-full flex items-center justify-center space-x-2 bg-main-purple text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 mb-2"
                      >
                        <Shield className="w-4 h-4" />
                        <span className="truncate">
                          {kennelName
                            ? t("nav.panelWithKennel", { kennelName })
                            : t("nav.adminPanel")}
                        </span>
                      </button>
                      {userKennelId && (
                        <button
                          onClick={() => {
                            navigate(`/canis/${userKennelId}`);
                            setIsMenuOpen(false);
                          }}
                          className="w-full flex items-center justify-center space-x-2 border border-main-purple text-main-purple px-6 py-3 rounded-xl font-semibold transition-all duration-300 mb-2 hover:bg-main-purple/10"
                        >
                          <Users className="w-4 h-4" />
                          <span>{t("nav.shelterProfile")}</span>
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={handleMobileLogout}
                    className="w-full flex items-center justify-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{t("nav.logout")}</span>
                  </button>
                </div>
              )}
            </div>
          </nav>
        )}
      </div>

      <ConfirmModal
        isOpen={showDismissConfirm}
        title={t("nav.hideAdvertiseTitle")}
        message={t("nav.hideAdvertiseMessage")}
        confirmLabel={t("actions.hide")}
        cancelLabel={t("actions.cancel")}
        onConfirm={() => {
          dismissAdvertiserNav();
          setShowDismissConfirm(false);
        }}
        onClose={() => setShowDismissConfirm(false)}
      />
    </nav>
  );
});

Navbar.displayName = "Navbar";

export default Navbar;
